import React from "react";
import { lazy, Suspense, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import CaptureEmailDialog from "components/common/dialogs/CaptureEmailDialog.js";
import useSeo from "utils/useSeo";
import { track, EVENTS } from "utils/analytics.js";
import { useEnsureExampleCatalog } from "store/functions/exampleCatalog.js";
import { selectExampleList } from "store/reducers/uiReducer";
import { getIndustry } from "components/domain/industries/industriesData.js";
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  Avatar,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import BarChartIcon from "@mui/icons-material/BarChart";
import RuleIcon from "@mui/icons-material/Rule";
import PublicIcon from "@mui/icons-material/Public";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

// Lazy-loaded so mapbox-gl stays out of the landing page's initial bundle; a
// static placeholder holds the layout until the map chunk arrives.
const HeroMiniMap = lazy(() => import("components/domain/home/HeroMiniMap"));

/*
The example the front page leads with: field service's flagship, read from
industriesData so the home page and /industries/field-service can never point at
different things, and so a slug rename has one place to happen.

Field service because it is the most-visited industry page (11 external visitors
in the fortnight to 2026-08-30, ahead of every other) and the ad campaigns lead
with it. Null-guarded: if the entry is ever renamed away, the button falls back
to the gallery rather than linking into a 404.
*/
const HERO_EXAMPLE_SLUG = getIndustry("field-service")?.flagship?.exampleSlug ?? null;

/*
Every header here must be a Locations column that is visible by default: the
column-mapping step only offers visible columns as targets, so anything else is
dropped on import without a word, and the file we hand out as "this is the
shape" would be teaching a shape the app silently discards.

Earliest/Latest Arrival Time were in the first version of this file and are the
reason for the note. They are minArrivalTimeStr/maxArrivalTimeStr, which ship
`isViewable: false` (time windows are off in a new scenario), so both columns
went in and neither arrived. sampleStopsCsvColumns.test.js checks the whole
header row against the app's own column definitions.
*/
export const SAMPLE_STOPS_CSV_CONTENT = [
  "Location Name,Latitude,Longitude,Demand,Service Duration,Depot?",
  "Central Depot,39.7392,-104.9903,0,0,true",
  "Stop 1 - Downtown Market,39.7531,-104.9998,2,15,false",
  "Stop 2 - Highlands Cafe,39.7612,-105.0115,1,10,false",
  "Stop 3 - Capitol Hill Books,39.7335,-104.9812,3,20,false",
  "Stop 4 - Cherry Creek Floral,39.7172,-104.9536,2,15,false",
  "Stop 5 - City Park Bakery,39.7456,-104.9501,1,10,false",
].join("\n");

export const SAMPLE_STOPS_CSV_DATA_URI = `data:text/csv;charset=utf-8,${encodeURIComponent(SAMPLE_STOPS_CSV_CONTENT)}`;

const HeroSection = () => {
  // The hero's figures come from the published example catalog rather than
  // being typed in, so they stay true as examples are added.
  useEnsureExampleCatalog();
  const examples = useSelector(selectExampleList);
  const exampleCount = examples.length;
  const cityCount = useMemo(
    () => new Set(examples.map((ex) => ex.geography?.city).filter(Boolean)).size,
    [examples]
  );

  return (
    <Box
      sx={{
        pt: { xs: 4, md: 8 },
        pb: { xs: 4, md: 8 },
        background: "linear-gradient(180deg, rgba(230, 252, 251, 0.5) 0%, #FFFFFF 100%)",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.5,
                borderRadius: "20px",
                backgroundColor: "primary.extraLight",
                // Small uppercase text on the tint: main is 3.6:1, extraDark 5.7:1.
                color: "primary.extraDark",
                mb: 3,
              }}
            >
              <BoltIcon sx={{ fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1 }}>
                Multi-stop fleet route optimization
              </Typography>
            </Box>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                fontWeight: 800,
                color: "dark.main",
                lineHeight: 1.1,
                mb: 3,
              }}
            >
              Multi-stop route planning <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>built for real fleet constraints</Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.125rem",
                color: "text.secondary",
                mb: 4,
                maxWidth: "520px",
                lineHeight: 1.6,
              }}
            >
              Sequence stops, respect vehicle capacities, and meet delivery time windows in seconds. Optimiciti routes your whole fleet and reports any stop it could not fit with the plan.
            </Typography>
            {/*
            The lead action opens a worked example, not an empty builder.

            Measured over the seven days to 2026-08-30, of 31 CTA clicks from
            visitors who were not us, 24 went to "Try it with your own stops" and
            exactly 1 to the examples — and that first button opens a five-step
            configuration wizard ending in an empty scenario. Nobody was shown
            the product working before being asked to do work, and 0 of those
            visitors reached a solve.

            The industry pages already lead with their flagship example, and there
            the split is even (2 and 2), which is the closest thing to evidence we
            have that people take the example when it is offered first.

            The slug is read from industriesData rather than written out again, so
            there is one place where "the example we put in front of people" lives
            and it cannot drift from the field-service page that also uses it.

            The labels changed on 2026-09-14 ("See it plan a real day" -> "Explore
            interactive demo", "Try it with your own stops" -> "Plan with your own
            fleet"); the cta_ids did not, so the dashboard series stays continuous.
            */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button
                component={Link}
                to={HERO_EXAMPLE_SLUG ? `/examples/${HERO_EXAMPLE_SLUG}` : "/use-cases"}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "hero_example", page: "home" })}
                sx={{
                  backgroundColor: "primary.main",
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "primary.extraDark" },
                }}
              >
                See a real day planned
              </Button>
              <Button
                component={Link}
                to="/model-builder"
                variant="outlined"
                size="large"
                onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "hero_model_builder", page: "home" })}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "primary.main",
                  color: "primary.main",
                  "&:hover": { borderColor: "primary.extraDark", backgroundColor: "primary.extraLight" },
                }}
              >
                Plan with your own fleet
              </Button>
            </Box>

            {/* Reassurance checklist. Each item must stay true: solves finish in
                seconds (3–60 s budget), the free starter plan needs no credit card,
                and uploads take .csv/.xlsx/.xls. */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 2.5 }}
              sx={{ mt: 2.5, mb: 0.5 }}
            >
              {[
                "Routes in seconds",
                "Free Starter plan — no card required",
                "Spreadsheet upload (Excel / CSV)",
              ].map((perk) => (
                <Stack key={perk} direction="row" spacing={0.75} alignItems="center">
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "primary.main" }} />
                  <Typography variant="body2" sx={{ fontSize: "0.8125rem", color: "text.secondary", fontWeight: 500 }}>
                    {perk}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            {/* The gallery stays one click away, but as a quiet link rather than
                a third button competing with the two above. Direct sample stops
                CSV provides immediate reassurance on required spreadsheet format. */}
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography
                component={Link}
                to="/use-cases"
                variant="body2"
                onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "hero_use_cases", page: "home" })}
                sx={{
                  color: "text.secondary",
                  textDecorationColor: "currentColor",
                  "&:hover": { color: "primary.main" },
                }}
              >
                or browse all {exampleCount > 0 ? exampleCount : ""} worked examples
              </Typography>
              <Typography
                component="span"
                variant="body2"
                sx={{ color: "text.disabled", display: { xs: "none", sm: "inline" } }}
              >
                •
              </Typography>
              <Typography
                component="a"
                href={SAMPLE_STOPS_CSV_DATA_URI}
                download="optimiciti_sample_stops.csv"
                variant="body2"
                onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "hero_download_sample_stops", page: "home" })}
                sx={{
                  color: "text.secondary",
                  textDecoration: "underline",
                  textDecorationColor: "currentColor",
                  "&:hover": { color: "primary.main" },
                }}
              >
                Download sample stops (.csv)
              </Typography>
            </Box>
            {/* Facts we can stand behind, read from the live example catalog so
                they can't go stale, rather than uptime/volume figures nobody
                outside the company can check. Hidden until the catalog loads —
                better no number than a wrong one. */}
            <Box sx={{ mt: 6, display: "flex", alignItems: "center", gap: 4 }}>
              {exampleCount > 0 && (
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: "dark.main" }}>{exampleCount}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>Worked examples</Typography>
                </Box>
              )}
              {cityCount > 0 && (
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: "dark.main" }}>{cityCount}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>Cities</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, color: "dark.main" }}>Free</Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>No sign-up required</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} sx={{ position: "relative" }}>
            {/* A real, view-only mini-map of the "courier" example (NYC flower
                delivery) in place of the old made-up before/after figures. */}
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Suspense
                fallback={
                  <Box
                    sx={{
                      height: { xs: 324, md: 384 },
                      borderRadius: 4,
                      border: "2px solid",
                      borderColor: "primary.light",
                      background:
                        "linear-gradient(135deg, rgba(0,146,139,0.10) 0%, rgba(0,146,139,0.03) 100%)",
                      boxShadow: "0 20px 40px rgba(0, 146, 139, 0.2)",
                    }}
                  />
                }
              >
                <HeroMiniMap />
              </Suspense>
            </Box>
            {/* Decorative blob behind the map card */}
            <Box
              sx={{
                position: "absolute",
                top: "-10%",
                right: "-10%",
                width: "60%",
                height: "60%",
                background: "radial-gradient(circle, rgba(0, 146, 139, 0.1) 0%, transparent 70%)",
                zIndex: 0,
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

const FeaturesSection = () => {
  // Six cards, no repeats: the earlier set spent two cards on the API
  // ("Browser or API" and "API access") and one of those undercut itself by
  // advertising a plan still on a waitlist. The API now has exactly one card,
  // and the freed slot goes to the planning features that actually
  // differentiate the solver — multi-day, resupply, mixed fleets.
  //
  // That single card then swung too far the other way. "post the same problem
  // over HTTP with public documentation" reads as an offer you can take up
  // today, and you cannot: keys are not being issued and the gateway refuses
  // every call. The developer portal says exactly that, in a banner across
  // every page, so the home page was the one place claiming otherwise. Beta is
  // stated here too now — someone who follows the link should find what this
  // page led them to expect.
  const features = [
    {
      title: "Hard constraints, not preferences",
      description: "Time windows, capacity, vehicle types, shift hours and pickup-before-dropoff are enforced. A stop that cannot be served is returned unassigned rather than quietly broken.",
      icon: <RuleIcon sx={{ color: "primary.main" }} />,
    },
    {
      title: "Multi-day routes, resupply, mixed fleets",
      description: "Jobs that run past a single day, vehicles that return to the depot to reload, and fleets where the vans don't match — planned together instead of patched by hand.",
      icon: <LocalShippingIcon sx={{ color: "primary.main" }} />,
    },
    {
      title: "Results you can audit",
      description: "Distance, duration and cost per vehicle, stop by stop, with arrival times — so you can see why a route is ordered the way it is.",
      icon: <BarChartIcon sx={{ color: "primary.main" }} />,
    },
    {
      title: "Fast enough to re-run",
      description: "Most scenarios come back in seconds, so changing an assumption and solving again is part of planning rather than an overnight batch.",
      icon: <BoltIcon sx={{ color: "primary.main" }} />,
    },
    {
      title: "Browser or API",
      description: "Enter data in the browser app, or post the same problem over HTTP — it is the same solver either way. The API is in beta and not open yet, but the documentation is public.",
      icon: <IntegrationInstructionsIcon sx={{ color: "primary.main" }} />,
    },
    {
      title: "Coordinates or a plain grid",
      description: "Latitude and longitude anywhere in the world, or an x-y grid for a warehouse floor or yard. Real road distances in North America, straight-line everywhere.",
      icon: <PublicIcon sx={{ color: "primary.main" }} />,
    },
  ];

  return (
    <Box sx={{ py: 8, backgroundColor: "#FFFFFF" }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, color: "dark.main", mb: 2 }}>
            What the solver handles
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: "600px", mx: "auto" }}>
            The constraints that decide whether a plan is usable — and what happens when one cannot be met.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  borderTop: "4px solid",
                  borderTopColor: "primary.main",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.02)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: "0 10px 25px rgba(0, 146, 139, 0.1)",
                    transform: "translateY(-6px)",
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: "primary.extraLight",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 3,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: "dark.main", mb: 1.5 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Enter your data",
      description: "Your stops, your vehicles, and the rules they run under — time windows, capacities, shift hours. Paste straight from a spreadsheet and map your columns once.",
    },
    {
      number: "02",
      title: "Run the solve",
      description: "The solver searches for the lowest-cost assignment and ordering that satisfies every constraint you set. Longer runs queue and come back to you; the page stays usable.",
    },
    {
      number: "03",
      title: "Review and export",
      description: "Every route, stop order and arrival time — plus anything the solver could not place, so nothing disappears without you knowing. Export to a file for your drivers.",
    },
  ];

  return (
    <Box sx={{ py: 8, backgroundColor: "grey.50" }}>
      <Container maxWidth="lg">
        {/* This section had no heading at all — the page jumped from the hero
            straight into three numbered circles, which also left a gap in the
            h1→h2 outline for screen readers and search engines. */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, color: "dark.main", mb: 2 }}>
            How it works
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: "600px", mx: "auto" }}>
            Three steps, no setup — the first solve takes minutes.
          </Typography>
        </Box>
        <Grid container spacing={4} justifyContent="center">
          {steps.map((step, index) => (
            <Grid item xs={12} md={4} key={index} sx={{ textAlign: "center" }}>
              <Box sx={{ position: "relative", mb: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: "auto",
                    backgroundColor: "primary.main",
                    fontSize: "1.5rem",
                    fontWeight: 800,
                    boxShadow: "0 8px 20px rgba(0, 146, 139, 0.2)",
                  }}
                >
                  {step.number}
                </Avatar>
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      display: { xs: "none", md: "block" },
                      position: "absolute",
                      top: "50%",
                      left: "calc(50% + 60px)",
                      width: "calc(100% - 120px)",
                      height: "2px",
                      backgroundColor: "primary.light",
                      zIndex: 0,
                    }}
                  />
                )}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "dark.main", mb: 2 }}>
                {step.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: "280px", mx: "auto", lineHeight: 1.6 }}>
                {step.description}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

const CTASection = () => {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <Box
      sx={{
        py: 8,
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.dark.main} 100%)`,
        color: "#fff",
        textAlign: "center",
      }}
    >
      <Container maxWidth="md">
        <Typography variant="h2" sx={{ fontWeight: 800, mb: 3, color: "#FFFFFF" }}>
          Try it with your own data
        </Typography>
        {/* The old line said "open an example" while both buttons below led
            elsewhere (model builder / sales) — the words and the buttons now
            make the same offer. "Until you want to save" is the real account
            boundary: solving is anonymous, saving is login-gated. */}
        <Typography variant="body1" sx={{ mb: 5, color: "#FFFFFF", fontSize: "1.125rem" }}>
          Paste your stops and run an optimization in your browser — free, and no account until you want to save your work.
        </Typography>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
          <Button
            component={Link}
            to="/model-builder"
            variant="contained"
            size="large"
            onClick={() => track(EVENTS.CTA_CLICKED, { cta_id: "bottom_model_builder", page: "home" })}
            sx={(theme) => ({
              backgroundColor: theme.palette.common.white,
              color: theme.palette.primary.main,
              px: 4,
              py: 1.5,
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              "&:hover": { backgroundColor: theme.palette.primary.extraLight },
            })}
          >
            Open route planner
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              track(EVENTS.CTA_CLICKED, { cta_id: "bottom_contact_sales", page: "home" });
              setContactOpen(true);
            }}
            sx={{
              borderColor: "rgba(255, 255, 255, 0.3)",
              color: "#fff",
              px: 4,
              py: 1.5,
              "&:hover": { borderColor: "#fff", backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            Contact sales
          </Button>
        </Box>
      </Container>

      <CaptureEmailDialog
        open={contactOpen}
        handleClose={() => setContactOpen(false)}
        title="Contact sales"
        content="Tell us about your needs and our team will be in touch shortly."
        source="contact_sales"
        submitButtonTitle="Send Message"
        useOrganization
        useMessage
      />
    </Box>
  );
};

export default function Index() {
  useSeo({
    description:
      "Plan vehicle routes and schedules around the constraints you set — time windows, capacities, vehicle types, shift hours. Free to try, no account needed.",
    canonicalPath: "/home",
  });
  return (
    // Deliberately a plain Box, not `component="main"`: the layout already
    // renders the page's single <main id="main-content"> landmark, and a nested
    // one is invalid HTML that gives screen readers two competing landmarks.
    // (It also picked up argon.min.css's bare `main { overflow: hidden }`, though
    // harmlessly here — this element's height follows its content, so there was
    // nothing to clip. See docs/accessibility.md.)
    <Box>
      <HeroSection />
      <HowItWorksSection />
      <FeaturesSection />
      <CTASection />
    </Box>
  );
}
